<template>
  <v-container class="page-container">
    <access-code-dialog
      v-model="showAccessCodeDialog"
      :loading="selecting"
      :error-message="accessCodeError"
      @submit="onAccessCodeSubmit"
      @cancel="onAccessCodeCancel"
    />

    <c-loader-status
      :loaders="{
        'no-initial-content no-error-content': [service.getSeriesById],
      }"
      :height="2"
    >
      <template v-if="series">
        <v-img
          v-if="bannerImage"
          :src="bannerImage"
          :alt="eventName"
          max-height="320"
          cover
          class="mb-4 rounded"
        />
        <div class="text-h4">{{ eventName }}</div>
        <div class="text-body-1 text-medium-emphasis mt-1">
          {{ dateRange }}<span v-if="location"> · {{ location }}</span>
        </div>
        <div
          v-if="descriptionHtml"
          class="text-body-2 mt-4 event-description"
          v-html="descriptionHtml"
          style="white-space: pre-wrap"
        />

        <v-card v-if="passTypes.length" variant="outlined" class="mt-6 pa-4">
          <div class="text-h6 mb-2">Passes</div>
          <v-list lines="two" density="comfortable">
            <v-list-item v-for="pt in passTypes" :key="pt.id ?? 0">
              <template #title>{{ pt.name }}</template>
              <template #subtitle>{{ passDates(pt) }}</template>
              <template #append>
                <div class="text-right">
                  <div class="font-weight-medium">
                    {{ formatCurrency(pt.price ?? 0) }}
                  </div>
                  <div class="text-caption text-medium-emphasis">
                    {{ isMultiDay(pt) ? "Multi-Day Pass" : "Single-Day Pass" }}
                  </div>
                </div>
              </template>
            </v-list-item>
          </v-list>
        </v-card>

        <v-row class="justify-center mt-8 mb-2">
          <v-btn
            color="secondary"
            size="x-large"
            :loading="selecting"
            @click="handleSelectPasses"
          >
            <v-icon start>fal fa-money-bill-wave</v-icon>
            Select Passes
          </v-btn>
        </v-row>
      </template>
      <v-alert v-else-if="loadError" type="error" variant="tonal" class="ma-4">
        {{ loadError }}
      </v-alert>
    </c-loader-status>

    <v-snackbar v-model="snackbar" :timeout="5000" color="error" location="top">
      {{ snackbarMsg }}
    </v-snackbar>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import AccessCodeDialog from "@common/event/access-code-dialog.vue";
import { PublicPurchaseServiceViewModel } from "@public/viewmodels.g";
import {
  PublicSeriesDto,
  PublicSeriesPassTypeDto,
  PassCategory,
} from "@public/models.g";
import { useSeriesPurchaseOrchestrator } from "@public/composables/useSeriesPurchaseOrchestrator";
import { formatCurrency, sanitizeHtml } from "@common/utils";
import { formatSeriesStartEndRange } from "@common/purchase/series-cart";
import { COMMON_ROUTE_NAMES } from "@common/routing";
import { useTitle } from "@common/composables/useTitle";

const props = defineProps<{ id: number }>();

const router = useRouter();
const service = new PublicPurchaseServiceViewModel();
const orchestrator = useSeriesPurchaseOrchestrator(service);

const series = ref<PublicSeriesDto | null>(null);
const loadError = ref<string | null>(null);
const selecting = ref(false);
const showAccessCodeDialog = ref(false);
const accessCodeError = ref<string | null>(null);
const snackbar = ref(false);
const snackbarMsg = ref("");

const eventName = computed(() => series.value?.eventName ?? "Event series");
useTitle(() => eventName.value);

const bannerImage = computed(() => series.value?.bannerImageSrc || null);

const location = computed(() => series.value?.location || "");

// Admin-authored HTML — must sanitize.
const descriptionHtml = computed(() => {
  const raw =
    series.value?.longDescription || series.value?.shortDescription || "";
  const trimmed = raw.trim();
  return trimmed ? sanitizeHtml(trimmed) : "";
});

// Server already filters to active pass types, ordered.
const passTypes = computed(() => series.value?.passTypes ?? []);

const dateRange = computed(() =>
  formatSeriesStartEndRange(series.value?.startDate, series.value?.endDate),
);

function datesPerPass(pt: PublicSeriesPassTypeDto): number {
  return Math.max(1, pt.dates?.length ?? 1);
}

function isMultiDay(pt: PublicSeriesPassTypeDto): boolean {
  return pt.passCategory === PassCategory.MultiDay && datesPerPass(pt) > 1;
}

function passDates(pt: PublicSeriesPassTypeDto): string {
  return (pt.dates ?? [])
    .map((d) =>
      new Date(d as any).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }),
    )
    .join(" · ");
}

async function load() {
  const c = service.getSeriesById;
  await c(props.id);
  if (c.wasSuccessful && c.result) {
    series.value = c.result;
  } else {
    loadError.value = c.message ?? "This event series is not available.";
  }
}
load();

function goToPurchase(publicId: string) {
  router.push({
    name: COMMON_ROUTE_NAMES.TicketPurchaseSingle,
    params: { publicId },
  });
}

async function handleSelectPasses() {
  selecting.value = true;
  accessCodeError.value = null;
  try {
    const result = await orchestrator.initiate(props.id);
    if (result.ok) {
      goToPurchase(result.purchase.publicId!);
    } else if (result.reason === "access-code") {
      showAccessCodeDialog.value = true;
    } else {
      snackbarMsg.value = result.message;
      snackbar.value = true;
    }
  } finally {
    selecting.value = false;
  }
}

async function onAccessCodeSubmit(code: string) {
  selecting.value = true;
  accessCodeError.value = null;
  try {
    const result = await orchestrator.initiate(props.id, code);
    if (result.ok) {
      showAccessCodeDialog.value = false;
      goToPurchase(result.purchase.publicId!);
    } else {
      accessCodeError.value = result.message;
    }
  } finally {
    selecting.value = false;
  }
}

function onAccessCodeCancel() {
  accessCodeError.value = null;
}
</script>
